import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Row, Col, Spinner, InputGroup, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faArrowRight, faTimes, faCheck, faCloudArrowUp, faFileLines,
  faTriangleExclamation, faSave, faDollarSign, faTruck, faPlus, faTrash,
  faLocationDot, faBox, faClipboardList, faCircleCheck, faMapPin
} from '@fortawesome/free-solid-svg-icons';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import '../styles/CreateBOLWizard.css';
import '../styles/modal-shared.css';
import moment from 'moment';

const STEPS = [
  { label: 'Location',     icon: faLocationDot  },
  { label: 'Freight',      icon: faBox          },
  { label: 'Instructions', icon: faClipboardList },
  { label: 'Confirm',      icon: faCircleCheck  },
];

const stateAbbreviations = {
  'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR',
  'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE',
  'florida': 'FL', 'georgia': 'GA', 'hawaii': 'HI', 'idaho': 'ID',
  'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS',
  'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
  'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS',
  'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV',
  'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
  'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK',
  'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT',
  'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV',
  'wisconsin': 'WI', 'wyoming': 'WY', 'district of columbia': 'DC'
};

const accessorialRates = [
  { service: 'Liftgate Pickup',          rate: 75.00 },
  { service: 'Liftgate Delivery',        rate: 75.00 },
  { service: 'Inside Pickup',            rate: 85.00 },
  { service: 'Inside Delivery',          rate: 85.00 },
  { service: 'Residential Pickup',       rate: 75.00 },
  { service: 'Residential Delivery',     rate: 75.00 },
  { service: 'Limited Access Pickup',    rate: 75.00 },
  { service: 'Limited Access Delivery',  rate: 75.00 },
  { service: 'Notification/Appointment', rate: 35.00 },
  { service: 'Sort and Segregate',       rate:  1.50, unit: 'per piece'      },
  { service: 'Detention',                rate: 35.00, unit: 'per 15 minutes' },
  { service: 'Storage',                  rate: 25.00, unit: 'per day'        },
];

const initialFormState = {
  origin: { date: '', name: '', address: '', city: '', state: '', zip: '' },
  destination: { date: '', name: '', address: '', city: '', state: '', zip: '' },
  freightItems: [],
  currentFreightItem: {
    customerRef: '', mawb: '', hawb: '', description: '',
    length: '', width: '', height: '', pieces: '', weight: '',
    weightUnit: 'lbs', dimensionUnit: 'in'
  },
  instructions: '',
  pocName: '',
  podEmail: '',
  documents: []
};

/* ─────────────────────────────────────────────────────────── */

const CreateBOLWizard = ({ show, onHide, onSuccess }) => {
  const [step, setStep]                     = useState(1);
  const [addresses, setAddresses]           = useState([]);
  const [isLoading, setIsLoading]           = useState(false);
  const [formData, setFormData]             = useState(initialFormState);
  const [uploadingDocuments, setUploadingDocuments] = useState([]);
  const [dateErrors, setDateErrors]         = useState({ origin: '', destination: '' });
  const [rateInfo, setRateInfo]             = useState(null);
  const [calculatingRate, setCalculatingRate] = useState(false);

  const today = moment().format('YYYY-MM-DD');

  /* ── helpers ── */
  const getStateAbbreviation = (stateName) => {
    if (!stateName) return '';
    if (stateName.length === 2 && /^[A-Za-z]{2}$/.test(stateName)) return stateName.toUpperCase();
    return stateAbbreviations[stateName.toLowerCase()] || stateName.substring(0, 2).toUpperCase();
  };

  const isStateValid = (state) => state && state.length === 2 && /^[A-Za-z]{2}$/.test(state);

  /* ── address book ── */
  const loadAddresses = useCallback(async () => {
    try {
      const { data } = await api.get('/api/bols/addresses');
      setAddresses(data);
    } catch {
      toast.error('Failed to load address book');
    }
  }, []);

  useEffect(() => {
    if (show) {
      setStep(1);
      setFormData(initialFormState);
      setUploadingDocuments([]);
      setRateInfo(null);
      loadAddresses();
    }
  }, [show, loadAddresses]);

  const handleAddressSelect = (addressId, type) => {
    if (addressId === '0') return;
    const sel = addresses.find(a => a.aid === parseInt(addressId));
    if (!sel) return;
    const state = getStateAbbreviation(sel.State || '');
    setFormData(prev => ({
      ...prev,
      [type]: { ...prev[type], name: sel.Name || '', address: sel.Address || '', city: sel.City || '', state, zip: sel.Zip || '' }
    }));
  };

  const saveToAddressBook = async (type) => {
    const addr = type === 'origin' ? formData.origin : formData.destination;
    if (!addr.name || !addr.address || !addr.city || !addr.state || !addr.zip) {
      toast.error('Please fill in all address fields before saving');
      return;
    }
    try {
      await api.post('/api/bols/addresses', { name: addr.name, address: addr.address, city: addr.city, state: addr.state, zip: addr.zip });
      await loadAddresses();
      toast.success('Address saved to address book');
    } catch {
      toast.error('Failed to save address to address book');
    }
  };

  const isAddressInBook = (type) => {
    const addr = type === 'origin' ? formData.origin : formData.destination;
    if (!addr.name || !addr.address || !addr.city || !addr.state || !addr.zip) return true;
    return addresses.some(a =>
      a.Name?.toUpperCase()    === addr.name?.toUpperCase()    &&
      a.Address?.toUpperCase() === addr.address?.toUpperCase() &&
      a.City?.toUpperCase()    === addr.city?.toUpperCase()    &&
      a.State?.toUpperCase()   === addr.state?.toUpperCase()   &&
      a.Zip                    === addr.zip
    );
  };

  /* ── field change handlers ── */
  const handleOriginChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, origin: { ...prev.origin, [name]: name === 'state' ? value.toUpperCase() : value } }));
  };

  const handleDestinationChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, destination: { ...prev.destination, [name]: name === 'state' ? value.toUpperCase() : value } }));
  };

  const handleFreightItemChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, currentFreightItem: { ...prev.currentFreightItem, [name]: value } }));
  };

  const handleInstructionsChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  /* ── date handlers ── */
  const handleOriginDateChange = (e) => {
    const newDate = e.target.value;
    setDateErrors(prev => ({ ...prev, origin: '' }));
    setFormData(prev => ({ ...prev, origin: { ...prev.origin, date: newDate } }));
    if (formData.destination.date && moment(formData.destination.date).isBefore(moment(newDate))) {
      setDateErrors(prev => ({ ...prev, destination: 'Delivery date cannot be before pickup date' }));
    } else {
      setDateErrors(prev => ({ ...prev, destination: '' }));
    }
  };

  const handleDestinationDateChange = (e) => {
    const newDate = e.target.value;
    setDateErrors(prev => ({ ...prev, destination: '' }));
    if (formData.origin.date && moment(newDate).isBefore(moment(formData.origin.date))) {
      setDateErrors(prev => ({ ...prev, destination: 'Delivery date cannot be before pickup date' }));
    }
    setFormData(prev => ({ ...prev, destination: { ...prev.destination, date: newDate } }));
  };

  /* ── freight ── */
  const addFreightItem = () => {
    const item = formData.currentFreightItem;
    if (!item.pieces || !item.weight) {
      toast.error('Pieces and Weight are required');
      return;
    }

    const toAdd = { ...item, freightDesc: item.description };

    if (toAdd.weightUnit === 'kg') {
      toAdd.originalWeight = toAdd.weight;
      toAdd.originalUnit   = 'kg';
      toAdd.weight         = (parseFloat(toAdd.weight) * 2.20462).toFixed(2);
    }
    if (toAdd.dimensionUnit === 'cm') {
      if (toAdd.length) { toAdd.originalLength = toAdd.length; toAdd.length = Math.ceil(parseFloat(toAdd.length) / 2.54).toString(); }
      if (toAdd.width)  { toAdd.originalWidth  = toAdd.width;  toAdd.width  = Math.ceil(parseFloat(toAdd.width)  / 2.54).toString(); }
      if (toAdd.height) { toAdd.originalHeight = toAdd.height; toAdd.height = Math.ceil(parseFloat(toAdd.height) / 2.54).toString(); }
      toAdd.originalDimensionUnit = 'cm';
      toAdd.dimensionUnit = 'in';
    }

    setFormData(prev => ({
      ...prev,
      freightItems: [...prev.freightItems, toAdd],
      currentFreightItem: { ...initialFormState.currentFreightItem }
    }));
    toast.success('Freight item added');
  };

  const removeFreightItem = (index) => {
    setFormData(prev => ({ ...prev, freightItems: prev.freightItems.filter((_, i) => i !== index) }));
  };

  /* ── file upload ── */
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    const oversized = files.filter(f => f.size > 5 * 1024 * 1024);
    if (oversized.length > 0) {
      toast.error(`Files exceed 5 MB limit: ${oversized.map(f => f.name).join(', ')}`);
      return;
    }
    const filesToUpload = files.filter(f => f.size <= 5 * 1024 * 1024);

    setUploadingDocuments(prev => [
      ...prev,
      ...filesToUpload.map(f => ({ name: f.name, size: f.size, status: 'uploading' }))
    ]);

    for (const file of filesToUpload) {
      const fd = new FormData();
      fd.append('file', file, file.name);
      try {
        const { data } = await api.post('/api/bols/upload', fd, {
          headers: { 'Content-Type': undefined },
        });
        if (!data?.fileName || !data?.url) throw new Error('Invalid response from server');
        setUploadingDocuments(prev => prev.filter(d => d.name !== file.name));
        setFormData(prev => ({
          ...prev,
          documents: [...prev.documents, {
            name: file.name, size: file.size, type: file.type,
            blobName: data.fileName, blobUrl: data.url, uploadSuccess: true
          }]
        }));
        toast.success(`${file.name} uploaded`);
      } catch (err) {
        setUploadingDocuments(prev => prev.map(d =>
          d.name === file.name ? { ...d, status: 'error', error: err.message || 'Upload failed' } : d
        ));
        toast.error(`Failed to upload ${file.name}`);
        setTimeout(() => setUploadingDocuments(prev => prev.filter(d => d.name !== file.name)), 5000);
      }
    }
    // reset input so same file can be re-selected
    e.target.value = '';
  };

  const removeDocument = (index) => {
    setFormData(prev => ({ ...prev, documents: prev.documents.filter((_, i) => i !== index) }));
  };

  /* ── rate calculation ── */
  const calculateRate = async () => {
    setCalculatingRate(true);
    setRateInfo(null);
    try {
      const payload = {
        origin:      { zip: formData.origin.zip, state: formData.origin.state },
        destination: { zip: formData.destination.zip, state: formData.destination.state },
        freightItems: formData.freightItems
      };
      const { data } = await api.post('/api/bols/calculate-rate', payload);
      if (data.success) {
        setRateInfo(data);
      } else {
        setRateInfo({ error: data.message });
      }
    } catch {
      setRateInfo({ error: 'Failed to calculate rate' });
    } finally {
      setCalculatingRate(false);
    }
  };

  useEffect(() => {
    if (step === 4 && formData.freightItems.length > 0) calculateRate();
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── submit ── */
  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      if (uploadingDocuments.length > 0) {
        toast.error('Please wait for all document uploads to complete');
        setIsLoading(false);
        return;
      }

      const fd = new FormData();
      fd.append('origin', JSON.stringify(formData.origin));
      fd.append('destination', JSON.stringify(formData.destination));

      const cleanedFreight = formData.freightItems.map(({ weightUnit, originalWeight, originalUnit, description, ...rest }) => ({
        ...rest, freightDesc: rest.freightDesc || description
      }));
      fd.append('freightItems', JSON.stringify(cleanedFreight));
      fd.append('instructions', formData.instructions || '');
      fd.append('pocName', formData.pocName || '');
      fd.append('podEmail', formData.podEmail || '');

      const successDocs = formData.documents.filter(d => d.uploadSuccess && d.blobUrl && d.blobName);
      if (successDocs.length > 0) {
        fd.append('documentMetadata', JSON.stringify(
          successDocs.map(d => ({ name: d.name, blobName: d.blobName, blobUrl: d.blobUrl, size: d.size, type: d.type }))
        ));
      }

      const { data } = await api.post('/api/bols/create', fd);

      if (data.documentResults) {
        const failed = data.documentResults.filter(r => !r.success);
        if (failed.length > 0) toast.error(`Warning: ${failed.length} document(s) failed to process`);
      }

      toast.success('BOL created successfully');
      setIsLoading(false);
      if (typeof onSuccess === 'function') onSuccess(data.bolId);
      onHide();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'An error occurred';
      toast.error(`Failed to create BOL: ${msg}`);
      setIsLoading(false);
    }
  };

  /* ── navigation ── */
  const nextStep = () => {
    if (step === 1) {
      const { origin, destination } = formData;
      const missingOrigin = ['date','name','address','city','state','zip'].filter(f => !origin[f]);
      const missingDest   = ['date','name','address','city','state','zip'].filter(f => !destination[f]);
      if (origin.state && !isStateValid(origin.state))      { toast.error('Origin state must be a 2-letter code'); return; }
      if (destination.state && !isStateValid(destination.state)) { toast.error('Destination state must be a 2-letter code'); return; }
      if (missingOrigin.length > 0 || missingDest.length > 0) {
        if (missingOrigin.length > 0 && missingDest.length > 0) {
          toast.error('Please fill in all required fields for Origin and Destination');
        } else if (missingOrigin.length > 0) {
          toast.error(`Missing Origin fields: ${missingOrigin.map(f => f.charAt(0).toUpperCase() + f.slice(1)).join(', ')}`);
        } else {
          toast.error(`Missing Destination fields: ${missingDest.map(f => f.charAt(0).toUpperCase() + f.slice(1)).join(', ')}`);
        }
        return;
      }
      if (new Date(origin.date) > new Date(destination.date)) {
        toast.error('Pickup date cannot be after delivery date');
        return;
      }
    }
    if (step === 2 && formData.freightItems.length === 0) {
      toast.error('Please add at least one freight item');
      return;
    }
    setStep(s => Math.min(s + 1, 4));
  };

  const prevStep = () => setStep(s => Math.max(s - 1, 1));

  const handleClose = () => {
    setStep(1);
    setFormData(initialFormState);
    setUploadingDocuments([]);
    setRateInfo(null);
    onHide();
  };

  /* ── keyboard: Enter in freight form adds item ── */
  const handleFreightKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); addFreightItem(); }
  };

  /* ───────────── render helpers ───────────── */

  // Called as a function (not used as <Component>), so inputs never lose focus on re-render
  const renderLocationPanel = (type, title, icon) => {
    const data     = formData[type];
    const errors   = type === 'origin' ? dateErrors.origin : dateErrors.destination;
    const onChange = type === 'origin' ? handleOriginChange : handleDestinationChange;
    const onDate   = type === 'origin' ? handleOriginDateChange : handleDestinationDateChange;
    const minDate  = type === 'destination' ? (formData.origin.date || today) : today;

    return (
      <div className="bol-panel h-100">
        <div className="bol-panel-header">
          <div className="bol-panel-header-icon"><FontAwesomeIcon icon={icon} /></div>
          <h6>{title}</h6>
        </div>
        <div className="bol-panel-body">
          {/* Address book */}
          <div className="mb-3">
            <label className="bol-form-label">Address Book</label>
            <Form.Select
              size="sm"
              className="bol-input"
              onChange={(e) => handleAddressSelect(e.target.value, type)}
              style={{ borderRadius: 8 }}
            >
              <option value="0">— Quick fill from address book —</option>
              {addresses.map(a => (
                <option key={a.aid} value={a.aid}>{a.Name}</option>
              ))}
            </Form.Select>
          </div>

          {/* Date */}
          <div className="mb-3">
            <label className="bol-form-label">Date <span className="text-danger">*</span></label>
            <Form.Control
              type="date"
              name="date"
              value={data.date}
              onChange={onDate}
              min={minDate}
              isInvalid={!!errors}
              className="bol-input"
              style={{ borderRadius: 8 }}
            />
            {errors && <div className="invalid-feedback d-block" style={{ fontSize: '0.75rem' }}>{errors}</div>}
          </div>

          {/* Name + save */}
          <div className="mb-3">
            <label className="bol-form-label">Name <span className="text-danger">*</span></label>
            <InputGroup size="sm">
              <Form.Control
                type="text"
                name="name"
                value={data.name}
                onChange={onChange}
                placeholder="Company / Contact name"
                className="bol-input"
                style={{ borderRadius: '8px 0 0 8px' }}
              />
              {!isAddressInBook(type) && (
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  title="Save to address book"
                  onClick={() => saveToAddressBook(type)}
                  style={{ borderRadius: '0 8px 8px 0', padding: '4px 10px', fontSize: '0.8rem' }}
                >
                  <FontAwesomeIcon icon={faSave} />
                </button>
              )}
            </InputGroup>
          </div>

          {/* Address */}
          <div className="mb-3">
            <label className="bol-form-label">Street Address <span className="text-danger">*</span></label>
            <Form.Control
              type="text"
              name="address"
              value={data.address}
              onChange={onChange}
              placeholder="123 Main St"
              className="bol-input"
              style={{ borderRadius: 8 }}
              size="sm"
            />
          </div>

          {/* City / State / Zip */}
          <Row className="g-2">
            <Col xs={5}>
              <label className="bol-form-label">City <span className="text-danger">*</span></label>
              <Form.Control
                type="text"
                name="city"
                value={data.city}
                onChange={onChange}
                placeholder="City"
                className="bol-input"
                style={{ borderRadius: 8 }}
                size="sm"
              />
            </Col>
            <Col xs={3}>
              <label className="bol-form-label">State <span className="text-danger">*</span></label>
              <Form.Control
                type="text"
                name="state"
                value={data.state}
                onChange={onChange}
                maxLength={2}
                placeholder="XX"
                isInvalid={data.state.length > 0 && !isStateValid(data.state)}
                className="bol-input text-uppercase"
                style={{ borderRadius: 8 }}
                size="sm"
              />
            </Col>
            <Col xs={4}>
              <label className="bol-form-label">Zip <span className="text-danger">*</span></label>
              <Form.Control
                type="text"
                name="zip"
                value={data.zip}
                onChange={onChange}
                maxLength={5}
                placeholder="12345"
                className="bol-input"
                style={{ borderRadius: 8 }}
                size="sm"
              />
            </Col>
          </Row>
        </div>
      </div>
    );
  };

  /* ─────────── JSX ─────────── */
  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      backdrop="static"
      centered
      dialogClassName="detail-modal bol-wizard-modal"
    >
      {/* ── HEADER ── */}
      <Modal.Header closeButton>
        <div className="modal-title-row">
          <div className="modal-header-icon">
            <FontAwesomeIcon icon={faTruck} />
          </div>
          <div className="modal-header-text">
            <h5>Create BOL Request</h5>
            <p className="modal-subtitle">Step {step} of 4 — {STEPS[step - 1].label}</p>
          </div>
        </div>
      </Modal.Header>

      {/* ── BODY ── */}
      <Modal.Body>
        {/* Progress indicator */}
        <div className="bol-steps">
          {STEPS.map((s, i) => {
            const num       = i + 1;
            const isActive  = num === step;
            const isDone    = num < step;
            return (
              <React.Fragment key={s.label}>
                {i > 0 && <div className={`bol-step-connector ${isDone ? 'done' : ''}`} />}
                <div className={`bol-step ${isActive ? 'active' : ''} ${isDone ? 'completed' : ''}`}>
                  <div className="bol-step-bubble">
                    {isDone ? <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.75rem' }} /> : num}
                  </div>
                  <span className="bol-step-label">{s.label}</span>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* ───── STEP 1: LOCATION ───── */}
        {step === 1 && (
          <div className="bol-step-body">
            <Row className="g-3">
              <Col md={6} className="d-flex flex-column">
                {renderLocationPanel('origin', 'Origin', faMapPin)}
              </Col>
              <Col md={6} className="d-flex flex-column">
                {renderLocationPanel('destination', 'Destination', faLocationDot)}
              </Col>
            </Row>
            <p className="text-muted mt-2" style={{ fontSize: '0.75rem' }}>
              Fields marked <span className="text-danger">*</span> are required.
            </p>
          </div>
        )}

        {/* ───── STEP 2: FREIGHT ───── */}
        {step === 2 && (
          <div className="bol-step-body">
            {/* Add item form */}
            <div className="bol-panel mb-3">
              <div className="bol-panel-header">
                <div className="bol-panel-header-icon"><FontAwesomeIcon icon={faPlus} /></div>
                <h6>Add Freight Item</h6>
              </div>
              <div className="bol-panel-body">
                {/* Row 1: references + description */}
                <Row className="g-2 mb-2">
                  <Col md={3} sm={6}>
                    <label className="bol-form-label">Customer Ref</label>
                    <Form.Control
                      type="text"
                      name="customerRef"
                      value={formData.currentFreightItem.customerRef}
                      onChange={handleFreightItemChange}
                      onKeyDown={handleFreightKeyDown}
                      placeholder="Ref #"
                      size="sm"
                      style={{ borderRadius: 8 }}
                    />
                  </Col>
                  <Col md={3} sm={6}>
                    <label className="bol-form-label">MAWB</label>
                    <Form.Control
                      type="text"
                      name="mawb"
                      value={formData.currentFreightItem.mawb}
                      onChange={handleFreightItemChange}
                      onKeyDown={handleFreightKeyDown}
                      placeholder="Master AWB"
                      size="sm"
                      style={{ borderRadius: 8 }}
                    />
                  </Col>
                  <Col md={3} sm={6}>
                    <label className="bol-form-label">HAWB</label>
                    <Form.Control
                      type="text"
                      name="hawb"
                      value={formData.currentFreightItem.hawb}
                      onChange={handleFreightItemChange}
                      onKeyDown={handleFreightKeyDown}
                      placeholder="House AWB"
                      size="sm"
                      style={{ borderRadius: 8 }}
                    />
                  </Col>
                  <Col md={3} sm={6}>
                    <label className="bol-form-label">Description</label>
                    <Form.Control
                      type="text"
                      name="description"
                      value={formData.currentFreightItem.description}
                      onChange={handleFreightItemChange}
                      onKeyDown={handleFreightKeyDown}
                      placeholder="Freight description"
                      size="sm"
                      style={{ borderRadius: 8 }}
                    />
                  </Col>
                </Row>

                {/* Row 2: dimensions + pieces + weight + add */}
                <div className="d-flex align-items-flex-end gap-2 flex-wrap">
                  {/* Dimensions group */}
                  <div>
                    <label className="bol-form-label d-block">Dimensions (L × W × H)</label>
                    <div className="d-flex align-items-center gap-1">
                      <Form.Control
                        type="number"
                        name="length"
                        value={formData.currentFreightItem.length}
                        onChange={handleFreightItemChange}
                        onKeyDown={handleFreightKeyDown}
                        min="0"
                        placeholder="L"
                        size="sm"
                        style={{ width: 60, borderRadius: 8 }}
                      />
                      <span className="text-muted" style={{ fontSize: '0.85rem' }}>×</span>
                      <Form.Control
                        type="number"
                        name="width"
                        value={formData.currentFreightItem.width}
                        onChange={handleFreightItemChange}
                        onKeyDown={handleFreightKeyDown}
                        min="0"
                        placeholder="W"
                        size="sm"
                        style={{ width: 60, borderRadius: 8 }}
                      />
                      <span className="text-muted" style={{ fontSize: '0.85rem' }}>×</span>
                      <Form.Control
                        type="number"
                        name="height"
                        value={formData.currentFreightItem.height}
                        onChange={handleFreightItemChange}
                        onKeyDown={handleFreightKeyDown}
                        min="0"
                        placeholder="H"
                        size="sm"
                        style={{ width: 60, borderRadius: 8 }}
                      />
                      {/* Dim unit toggle */}
                      <div className="unit-toggle ms-1" style={{ width: 80 }}>
                        <input type="radio" id="dimIn" name="dimensionUnit" value="in"
                          checked={formData.currentFreightItem.dimensionUnit === 'in'}
                          onChange={handleFreightItemChange} />
                        <label htmlFor="dimIn">in</label>
                        <input type="radio" id="dimCm" name="dimensionUnit" value="cm"
                          checked={formData.currentFreightItem.dimensionUnit === 'cm'}
                          onChange={handleFreightItemChange} />
                        <label htmlFor="dimCm">cm</label>
                      </div>
                    </div>
                  </div>

                  {/* Vertical divider */}
                  <div style={{ width: 1, background: '#e2e8f0', alignSelf: 'stretch', margin: '0 4px', marginTop: 22 }} />

                  {/* Pieces */}
                  <div>
                    <label className="bol-form-label d-block">Pieces <span className="text-danger">*</span></label>
                    <Form.Control
                      type="number"
                      name="pieces"
                      value={formData.currentFreightItem.pieces}
                      onChange={handleFreightItemChange}
                      onKeyDown={handleFreightKeyDown}
                      min="1"
                      placeholder="0"
                      size="sm"
                      style={{ width: 70, borderRadius: 8 }}
                    />
                  </div>

                  {/* Weight */}
                  <div>
                    <label className="bol-form-label d-block">Weight <span className="text-danger">*</span></label>
                    <div className="d-flex align-items-center gap-1">
                      <Form.Control
                        type="number"
                        name="weight"
                        value={formData.currentFreightItem.weight}
                        onChange={handleFreightItemChange}
                        onKeyDown={handleFreightKeyDown}
                        min="0"
                        placeholder="0"
                        size="sm"
                        style={{ width: 80, borderRadius: 8 }}
                      />
                      <div className="unit-toggle" style={{ width: 90 }}>
                        <input type="radio" id="wLbs" name="weightUnit" value="lbs"
                          checked={formData.currentFreightItem.weightUnit === 'lbs'}
                          onChange={handleFreightItemChange} />
                        <label htmlFor="wLbs">lbs</label>
                        <input type="radio" id="wKg" name="weightUnit" value="kg"
                          checked={formData.currentFreightItem.weightUnit === 'kg'}
                          onChange={handleFreightItemChange} />
                        <label htmlFor="wKg">kg</label>
                      </div>
                    </div>
                  </div>

                  {/* Add button */}
                  <div className="ms-auto" style={{ marginTop: 20 }}>
                    <button
                      className="detail-btn detail-btn--primary"
                      onClick={addFreightItem}
                      disabled={!formData.currentFreightItem.pieces || !formData.currentFreightItem.weight}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      Add Item
                    </button>
                  </div>
                </div>

                <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.72rem' }}>
                  Tip: Press <kbd>Enter</kbd> in any field to quickly add the item.
                  Weights in kg and dimensions in cm are automatically converted.
                </p>
              </div>
            </div>

            {/* Freight items table */}
            <div className="bol-panel">
              <div className="bol-panel-header">
                <div className="bol-panel-header-icon"><FontAwesomeIcon icon={faBox} /></div>
                <h6>Freight Items ({formData.freightItems.length})</h6>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="bol-freight-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Cust Ref</th>
                      <th>MAWB</th>
                      <th>HAWB</th>
                      <th>Description</th>
                      <th>Dimensions</th>
                      <th>Pieces</th>
                      <th>Weight</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.freightItems.length === 0 ? (
                      <tr><td colSpan={9} className="bol-empty-row">No freight items added yet</td></tr>
                    ) : (
                      formData.freightItems.map((item, i) => (
                        <tr key={i}>
                          <td style={{ color: '#94a3b8', fontWeight: 600 }}>{i + 1}</td>
                          <td>{item.customerRef || '—'}</td>
                          <td>{item.mawb || '—'}</td>
                          <td>{item.hawb || '—'}</td>
                          <td>{item.freightDesc || '—'}</td>
                          <td>
                            {item.length && item.width && item.height ? (
                              item.originalDimensionUnit === 'cm'
                                ? <span title={`${item.length}×${item.width}×${item.height} in`}>{item.originalLength}×{item.originalWidth}×{item.originalHeight} cm</span>
                                : `${item.length}×${item.width}×${item.height} in`
                            ) : '—'}
                          </td>
                          <td>{item.pieces}</td>
                          <td>
                            {item.originalWeight
                              ? <span title={`${item.weight} lbs`}>{item.originalWeight} kg</span>
                              : `${item.weight} lbs`}
                          </td>
                          <td>
                            <button
                              className="detail-btn"
                              style={{ padding: '4px 8px', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 6, background: '#fff5f5' }}
                              onClick={() => removeFreightItem(i)}
                              title="Remove item"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ───── STEP 3: INSTRUCTIONS & DOCUMENTS ───── */}
        {step === 3 && (
          <div className="bol-step-body">
            <Row className="g-3">
              {/* Documents */}
              <Col md={6}>
                <div className="bol-panel h-100">
                  <div className="bol-panel-header">
                    <div className="bol-panel-header-icon"><FontAwesomeIcon icon={faCloudArrowUp} /></div>
                    <h6>Upload Documents</h6>
                  </div>
                  <div className="bol-panel-body">
                    <label htmlFor="bol-doc-upload" className="bol-upload-zone d-block">
                      <div className="bol-upload-icon"><FontAwesomeIcon icon={faCloudArrowUp} /></div>
                      <div className="bol-upload-text">Click to select files or drag &amp; drop</div>
                      <div className="bol-upload-hint">PDF, JPG, PNG, DOC, DOCX — max 5 MB each</div>
                      <input
                        id="bol-doc-upload"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        multiple
                        onChange={handleFileUpload}
                      />
                    </label>

                    {/* Uploaded files */}
                    {(formData.documents.length > 0 || uploadingDocuments.length > 0) && (
                      <div className="mt-3">
                        {formData.documents.map((doc, i) => (
                          <div key={`doc-${i}`} className="bol-file-row">
                            <FontAwesomeIcon icon={faFileLines} style={{ color: '#3b82f6', fontSize: '0.9rem' }} />
                            <span className="bol-file-name">{doc.name}</span>
                            <span className="bol-file-size">{Math.round(doc.size / 1024)} KB</span>
                            <button
                              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0 4px', fontSize: '0.85rem' }}
                              onClick={() => removeDocument(i)}
                              title="Remove"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        ))}
                        {uploadingDocuments.map((doc, i) => (
                          <div key={`up-${i}`} className="bol-file-row">
                            {doc.status === 'uploading'
                              ? <Spinner animation="border" size="sm" style={{ color: '#3b82f6' }} />
                              : <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#ef4444' }} />}
                            <span className="bol-file-name">{doc.name}</span>
                            <span className="bol-file-status" style={{ color: doc.status === 'uploading' ? '#3b82f6' : '#ef4444' }}>
                              {doc.status === 'uploading' ? 'Uploading…' : (doc.error || 'Failed')}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Col>

              {/* Instructions */}
              <Col md={6}>
                <div className="bol-panel h-100">
                  <div className="bol-panel-header">
                    <div className="bol-panel-header-icon"><FontAwesomeIcon icon={faClipboardList} /></div>
                    <h6>Special Instructions</h6>
                  </div>
                  <div className="bol-panel-body">
                    <div className="mb-3">
                      <label className="bol-form-label">Instructions</label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="instructions"
                        value={formData.instructions}
                        onChange={handleInstructionsChange}
                        placeholder="e.g. HAZMAT, Liftgate required, Temperature sensitive…"
                        style={{ borderRadius: 8, fontSize: '0.88rem' }}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="bol-form-label">Your Name</label>
                      <Form.Control
                        type="text"
                        name="pocName"
                        value={formData.pocName}
                        onChange={handleInstructionsChange}
                        placeholder="Point of contact name"
                        size="sm"
                        style={{ borderRadius: 8 }}
                      />
                    </div>
                    <div>
                      <label className="bol-form-label">POD Email</label>
                      <Form.Control
                        type="email"
                        name="podEmail"
                        value={formData.podEmail}
                        onChange={handleInstructionsChange}
                        placeholder="Delivery confirmation email"
                        size="sm"
                        style={{ borderRadius: 8 }}
                      />
                      <div className="text-muted mt-1" style={{ fontSize: '0.72rem' }}>
                        Receive an email when delivery is confirmed.
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}

        {/* ───── STEP 4: CONFIRMATION ───── */}
        {step === 4 && (
          <div className="bol-step-body">
            <Row className="g-3">
              {/* Summary */}
              <Col md={5}>
                <div className="bol-panel">
                  <div className="bol-panel-header">
                    <div className="bol-panel-header-icon"><FontAwesomeIcon icon={faCircleCheck} /></div>
                    <h6>Summary</h6>
                  </div>
                  <div className="bol-panel-body">
                    <ul className="bol-confirm-list">
                      <li className="bol-confirm-item">
                        <div className="bol-confirm-check"><FontAwesomeIcon icon={faCheck} /></div>
                        <div>
                          <span className="bol-confirm-key">Origin:</span>
                          {formData.origin.name} — {formData.origin.city}, {formData.origin.state}
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{formData.origin.date}</div>
                        </div>
                      </li>
                      <li className="bol-confirm-item">
                        <div className="bol-confirm-check"><FontAwesomeIcon icon={faCheck} /></div>
                        <div>
                          <span className="bol-confirm-key">Destination:</span>
                          {formData.destination.name} — {formData.destination.city}, {formData.destination.state}
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{formData.destination.date}</div>
                        </div>
                      </li>
                      <li className="bol-confirm-item">
                        <div className="bol-confirm-check"><FontAwesomeIcon icon={faCheck} /></div>
                        <div>
                          <span className="bol-confirm-key">Freight:</span>
                          {formData.freightItems.length} item{formData.freightItems.length !== 1 ? 's' : ''},&nbsp;
                          {formData.freightItems.reduce((s, i) => s + parseFloat(i.weight || 0), 0).toFixed(0)} lbs total
                        </div>
                      </li>
                      <li className="bol-confirm-item">
                        <div className="bol-confirm-check"><FontAwesomeIcon icon={faCheck} /></div>
                        <div>
                          <span className="bol-confirm-key">Documents:</span>
                          {formData.documents.length > 0 ? `${formData.documents.length} file(s) attached` : 'None'}
                        </div>
                      </li>
                      <li className="bol-confirm-item">
                        <div className="bol-confirm-check"><FontAwesomeIcon icon={faCheck} /></div>
                        <div>
                          <span className="bol-confirm-key">Instructions:</span>
                          {formData.instructions ? formData.instructions.substring(0, 60) + (formData.instructions.length > 60 ? '…' : '') : 'None'}
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Rate estimate */}
                <div className="bol-rate-card mt-3">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <FontAwesomeIcon icon={faDollarSign} style={{ color: '#0369a1' }} />
                    <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#0369a1' }}>Estimated Rate</span>
                  </div>
                  {calculatingRate ? (
                    <div className="text-center py-2">
                      <Spinner animation="border" size="sm" style={{ color: '#0369a1' }} />
                      <span className="ms-2" style={{ fontSize: '0.82rem', color: '#0369a1' }}>Calculating…</span>
                    </div>
                  ) : rateInfo ? (
                    rateInfo.error ? (
                      <div style={{ fontSize: '0.82rem', color: '#b45309' }}>{rateInfo.error}</div>
                    ) : (
                      <>
                        <div className="bol-rate-row">
                          <span>Base Rate</span>
                          <span>${rateInfo.rate?.toFixed(2)}</span>
                        </div>
                        <div className="bol-rate-row">
                          <span>Fuel Surcharge</span>
                          <span>${rateInfo.fsc?.toFixed(2)}</span>
                        </div>
                        <div className="bol-rate-row">
                          <span>Estimated Total</span>
                          <span>${rateInfo.total?.toFixed(2)}</span>
                        </div>
                      </>
                    )
                  ) : null}
                  <div className="bol-rate-note">Estimated rate only — actual charges may vary.</div>
                </div>
              </Col>

              {/* Accessorial rates */}
              <Col md={7}>
                <div className="bol-panel">
                  <div className="bol-panel-header">
                    <div className="bol-panel-header-icon"><FontAwesomeIcon icon={faDollarSign} /></div>
                    <h6>Available Accessorial Services</h6>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="bol-freight-table">
                      <thead>
                        <tr>
                          <th>Service</th>
                          <th style={{ textAlign: 'right' }}>Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accessorialRates.map((r, i) => (
                          <tr key={i}>
                            <td>{r.service}</td>
                            <td style={{ textAlign: 'right' }}>
                              ${r.rate.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                              {r.unit && <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}> {r.unit}</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ padding: '8px 16px', fontSize: '0.72rem', color: '#94a3b8' }}>
                    * Additional charges may apply. Contact customer service for details.
                  </div>
                </div>

                <Alert variant="info" className="mt-3" style={{ fontSize: '0.82rem', borderRadius: 10 }}>
                  <strong>Ready to submit?</strong> Once submitted, a BOL will be created and our team will be notified.
                </Alert>
              </Col>
            </Row>
          </div>
        )}
      </Modal.Body>

      {/* ── FOOTER ── */}
      <Modal.Footer>
        <button className="detail-btn detail-btn--ghost" onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} />
          Cancel
        </button>
        {step > 1 && (
          <button className="detail-btn detail-btn--secondary" onClick={prevStep}>
            <FontAwesomeIcon icon={faArrowLeft} />
            Back
          </button>
        )}
        {step < 4 ? (
          <button className="detail-btn detail-btn--primary" onClick={nextStep}>
            Next
            <FontAwesomeIcon icon={faArrowRight} />
          </button>
        ) : (
          <button
            className="detail-btn detail-btn--primary"
            onClick={handleSubmit}
            disabled={isLoading}
            style={isLoading ? { opacity: 0.7 } : {}}
          >
            {isLoading
              ? <><Spinner animation="border" size="sm" className="me-1" /> Creating…</>
              : <><FontAwesomeIcon icon={faCheck} /> Submit BOL Request</>}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CreateBOLWizard;
